import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import {
  AnalyticsContext,
  AnalyticsProvider,
} from './analytics-provider.interface';
import './analytics.types';

/** Google Analytics (gtag.js) implementation of AnalyticsProvider. */
@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsProvider implements AnalyticsProvider {
  readonly name = 'google';

  private tenantName = 'unknown';
  private readonly config = environment.analytics?.google;

  isEnabled(): boolean {
    return !!this.config?.enabled && !!this.config?.measurementId;
  }

  init(context: AnalyticsContext): void {
    if (typeof window === 'undefined') return;
    const measurementId = this.config!.measurementId;
    this.tenantName = context.tenantName || 'unknown';

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer!.push(arguments);
    };

    this.gtag('js', new Date());
    // tenant_name rides every hit and as a user property for segmentation.
    this.gtag('set', { tenant_name: this.tenantName });
    this.gtag('set', 'user_properties', { tenant_name: this.tenantName });
    // Router owns page views, so suppress gtag's automatic one.
    this.gtag('config', measurementId, { send_page_view: false });
  }

  trackPageView(url: string): void {
    this.gtag('event', 'page_view', {
      page_path: url,
      tenant_name: this.tenantName,
    });
  }

  trackEvent(eventName: string, params: Record<string, any>): void {
    this.gtag('event', eventName, {
      ...params,
      tenant_name: params['tenant_name'] ?? this.tenantName,
    });
  }

  private gtag(...args: any[]): void {
    window.gtag?.(...args);
  }
}
